import React, { useEffect, useState, useCallback } from 'react';
import { Plus, ChevronLeft, ChevronRight, X, Search, Printer, AlertCircle, Trash2 } from 'lucide-react';
import { getJadwalKerja, createJadwalKerja, updateJadwalKerja, deleteJadwalKerja, createJadwalBulk, getKaryawan } from '../api';
import { useAuth } from '../AuthContext';

const todayStr = () => new Date().toISOString().slice(0, 10);
const monthStr = (d) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');

const MONTHS = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const DAYS = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Status codes for jadwal cells — stored in keterangan field
const STATUS_OPTIONS = [
  { code: 'H', label: 'Hadir', bg: '#d1fae5', text: '#065f46' },
  { code: 'I', label: 'Izin', bg: '#fef3c7', text: '#92400e' },
  { code: 'S', label: 'Sakit', bg: '#fee2e2', text: '#991b1b' },
  { code: 'C', label: 'Cuti', bg: '#dbeafe', text: '#1e40af' },
  { code: 'O', label: 'Off/Libur', bg: '#fef2f2', text: '#dc2626' },
  { code: 'A', label: 'Alpha', bg: '#fecaca', text: '#7f1d1d' },
  { code: 'B', label: 'Backup', bg: '#f0f4ff', text: '#3b82f6' },
];

const STATUS_MAP = {};
STATUS_OPTIONS.forEach(s => { STATUS_MAP[s.code] = s; });

function getStatusStyle(code) {
  const s = STATUS_MAP[code];
  return s || STATUS_MAP['H'];
}

export default function JadwalKerja() {
  const { hasMenuAction } = useAuth();
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [karyawan, setKaryawan] = useState([]);
  const [monthData, setMonthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    karyawan_ids: [], tanggal_awal: todayStr(), tanggal_akhir: todayStr(),
    jam_masuk: '07:00', jam_pulang: '15:00',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  // Status picker
  const [showPicker, setShowPicker] = useState(false);
  const [pickerData, setPickerData] = useState({ karyawanId: null, tanggal: null, entryId: null, nama: '', currentStatus: null, jamMasuk: '07:00', jamPulang: '15:00' });
  const [jamEdit, setJamEdit] = useState({ masuk: '07:00', pulang: '15:00' });
  // Filters
  const [filterDate, setFilterDate] = useState(''); // empty = all days
  const [filterStatus, setFilterStatus] = useState(''); // empty = all statuses
  const [filterBagian, setFilterBagian] = useState(''); // empty = all bagian

  const selectedMonth = monthStr(new Date(currentYear, currentMonth, 1));

  // Load data for the selected month
  const loadMonth = useCallback(() => {
    setLoading(true);
    Promise.all([
      getJadwalKerja({ bulan: selectedMonth }),
      getKaryawan(),
    ])
      .then(([jadwalRes, karRes]) => {
        const jadwal = jadwalRes.data || [];
        const kar = (karRes.data || []).filter(k => k.status === 'Aktif');
        setMonthData(jadwal);
        setKaryawan(kar);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [selectedMonth]);

  useEffect(() => { loadMonth(); }, [loadMonth]);

  // Build matrix: karyawan_id → { date → entry }
  const scheduleMap = {};
  monthData.forEach(e => {
    if (!scheduleMap[e.karyawan_id]) scheduleMap[e.karyawan_id] = {};
    scheduleMap[e.karyawan_id][e.tanggal] = e;
  });

  const days = daysInMonth(currentYear, currentMonth);
  const today = todayStr();

  // Unique bagian list for filter
  const bagianList = [...new Set(karyawan.map(k => k.bagian_name).filter(Boolean))].sort();

  // Filter karyawan by name, date, status, and bagian
  const filteredKaryawan = karyawan.filter(k => {
    // Search filter
    if (search && !k.nama?.toLowerCase().includes(search.toLowerCase()) && !k.nik?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    // Bagian filter
    if (filterBagian && k.bagian_name !== filterBagian) {
      return false;
    }
    // Date + Status filter
    if (filterDate && filterStatus) {
      const entry = scheduleMap[k.id]?.[filterDate];
      return entry && (entry.keterangan || 'H') === filterStatus;
    }
    // Date filter only
    if (filterDate) {
      return !!scheduleMap[k.id]?.[filterDate];
    }
    // Status filter only
    if (filterStatus) {
      const entries = scheduleMap[k.id] || {};
      return Object.values(entries).some(e => (e.keterangan || 'H') === filterStatus);
    }
    return true;
  });

  // ===== Status picker: open on cell click =====
  const openStatusPicker = (karyawanId, tanggal) => {
    const entry = scheduleMap[karyawanId] && scheduleMap[karyawanId][tanggal];
    const nama = karyawan.find(k => k.id === karyawanId)?.nama || 'Karyawan';
    const jamMasuk = entry?.jam_masuk || '07:00';
    const jamPulang = entry?.jam_pulang || '15:00';
    setPickerData({
      karyawanId, tanggal,
      entryId: entry ? entry.id : null,
      currentStatus: entry ? (entry.keterangan || 'H') : 'H',
      nama, jamMasuk, jamPulang,
    });
    setJamEdit({ masuk: jamMasuk, pulang: jamPulang });
    setShowPicker(true);
  };

  // Handle status selection from picker
  const handleStatusPick = async (code) => {
    const { karyawanId, tanggal, entryId, currentStatus } = pickerData;
    const jamMasuk = jamEdit.masuk || '07:00';
    const jamPulang = jamEdit.pulang || '15:00';
    setShowPicker(false);

    if (entryId) {
      if (code === currentStatus) {
        // Same status clicked again — confirm delete
        if (!window.confirm(`Hapus jadwal ${pickerData.nama} tanggal ${tanggal}?`)) return;
        try {
          await deleteJadwalKerja(entryId);
          loadMonth();
        } catch (err) {
          alert('Gagal menghapus: ' + (err.response?.data?.error || err.message));
        }
      } else {
        // Change status — update keterangan field, preserve jam
        try {
          await updateJadwalKerja(entryId, {
            karyawan_id: karyawanId,
            tanggal: tanggal,
            shift: 'Pagi',
            jam_masuk: jamMasuk,
            jam_pulang: jamPulang,
            keterangan: code,
          });
          loadMonth();
        } catch (err) {
          alert('Gagal update: ' + (err.response?.data?.error || err.message));
        }
      }
    } else {
      // Create new jadwal with selected status
      try {
        await createJadwalKerja({
          karyawan_id: karyawanId,
          tanggal,
          shift: 'Pagi',
          jam_masuk: jamMasuk,
          jam_pulang: jamPulang,
          keterangan: code,
        });
        loadMonth();
      } catch (err) {
        alert('Gagal menambah: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  // Handle jam save independently
  const handleJamSave = async () => {
    const { karyawanId, tanggal, entryId } = pickerData;
    if (!entryId) return;
    const jamMasuk = jamEdit.masuk || '07:00';
    const jamPulang = jamEdit.pulang || '15:00';
    try {
      await updateJadwalKerja(entryId, {
        karyawan_id: karyawanId,
        tanggal: tanggal,
        shift: 'Pagi',
        jam_masuk: jamMasuk,
        jam_pulang: jamPulang,
        keterangan: pickerData.currentStatus || 'H',
      });
      setShowPicker(false);
      loadMonth();
    } catch (err) {
      alert('Gagal simpan jam: ' + (err.response?.data?.error || err.message));
    }
  };

  // ===== Bulk add form =====
  const toggleKaryawan = (id) => {
    setAddForm(prev => ({
      ...prev,
      karyawan_ids: prev.karyawan_ids.includes(id)
        ? prev.karyawan_ids.filter(k => k !== id)
        : [...prev.karyawan_ids, id],
    }));
  };

  const selectAllKaryawan = () => {
    setAddForm(prev => ({
      ...prev,
      karyawan_ids: karyawan.map(k => k.id),
    }));
  };

  const handleBulkAdd = async () => {
    setError('');
    if (addForm.karyawan_ids.length === 0) {
      setError('Pilih minimal 1 karyawan'); return;
    }
    setSaving(true);
    try {
      const res = await createJadwalBulk({
        karyawan_ids: addForm.karyawan_ids,
        tanggal_awal: addForm.tanggal_awal,
        tanggal_akhir: addForm.tanggal_akhir,
        shift: 'Pagi',
        jam_masuk: addForm.jam_masuk || '07:00',
        jam_pulang: addForm.jam_pulang || '15:00',
      });
      alert(res.data?.message || 'Jadwal berhasil dibuat');
      setShowAddModal(false);
      loadMonth();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal membuat jadwal');
    } finally { setSaving(false); }
  };

  // ===== Navigation =====
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  const goToday = () => {
    const d = new Date();
    setCurrentMonth(d.getMonth());
    setCurrentYear(d.getFullYear());
    setFilterDate(today);
  };

  // Stats
  const totalEntries = monthData.length;
  const uniqueKaryawan = [...new Set(monthData.map(d => d.karyawan_id))].length;
  const uniqueDays = [...new Set(monthData.map(d => d.tanggal))].length;

  // Today's attendance summary (when filterDate selected)
  const dateForSummary = filterDate || today;
  const todayEntries = Object.values(scheduleMap).map(m => m[dateForSummary]).filter(Boolean);
  const todayStatusCounts = {};
  STATUS_OPTIONS.forEach(s => { todayStatusCounts[s.code] = 0; });
  todayEntries.forEach(e => {
    const code = e.keterangan || 'H';
    if (todayStatusCounts[code] !== undefined) todayStatusCounts[code]++;
  });
  const filteredCount = filteredKaryawan.length;

  // ===== PDF Export =====
  const generateMonthlyPDF = () => {
    if (!monthData.length) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const primary = [17, 205, 239];
    const dark = [23, 43, 77];
    const gray = [136, 152, 170];

    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 38, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('JADWAL KERJA BULANAN', margin, 18);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const bulanLabel = MONTHS[currentMonth] + ' ' + currentYear;
    doc.text(`Periode: ${bulanLabel} | Total: ${totalEntries} jadwal`, margin, 28);
    doc.setFont('helvetica', 'bold');
    doc.text('Layanan Synexa', pageW - margin, 18, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Sistem Informasi Armada', pageW - margin, 25, { align: 'right' });

    const startY = 48;
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 22, 3, 3, 'F');
    const statW = (pageW - 2 * margin - 15) / 3;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text('Total Jadwal', margin + 8, startY + 9);
    doc.text('Hari Kerja', margin + statW + 13, startY + 9);
    doc.text('Karyawan', margin + 2 * (statW + 5) + 8, startY + 9);
    doc.setTextColor(...dark);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`${totalEntries}`, margin + 8, startY + 18);
    doc.text(`${uniqueDays} hari`, margin + statW + 13, startY + 18);
    doc.text(`${uniqueKaryawan} orang`, margin + 2 * (statW + 5) + 8, startY + 18);

    const grouped = {};
    monthData.forEach(d => {
      const tgl = d.tanggal || '-';
      if (!grouped[tgl]) grouped[tgl] = [];
      grouped[tgl].push(d);
    });

    const headers = [['Tanggal', 'NIK', 'Nama', 'Bagian', 'Jam Masuk', 'Jam Pulang', 'Keterangan']];
    const rows = [];
    Object.keys(grouped).sort().forEach(tgl => {
      const items = grouped[tgl];
      const tglLabel = new Date(tgl + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      items.forEach((d, idx) => {
        rows.push([
          idx === 0 ? tglLabel : '',
          d.nik || '-', d.nama || '-', d.bagian_name || '-',
          d.jam_masuk || '07:00', d.jam_pulang || '15:00', d.keterangan || '-',
        ]);
      });
    });

    autoTable(doc, {
      startY: startY + 30,
      margin: { left: margin, right: margin },
      head: headers, body: rows,
      styles: { fontSize: 7, cellPadding: 2.5, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
      headStyles: { fillColor: [94, 114, 228], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
      bodyStyles: { fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 55, fontStyle: 'bold', textColor: [23, 43, 77] },
        1: { cellWidth: 25, halign: 'center' }, 2: { cellWidth: 35 },
        3: { cellWidth: 25, halign: 'center' }, 4: { halign: 'center', cellWidth: 20 },
        5: { halign: 'center', cellWidth: 20 },
      },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const h = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, h - 15, pageW - margin, h - 15);
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      const dateStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`Dicetak pada: ${dateStr}`, margin, h - 8);
      doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, h - 8, { align: 'right' });
    }
    doc.save(`Jadwal_Kerja_${selectedMonth}.pdf`);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Jadwal Kerja</h2>
            <p className="page-desc">{MONTHS[currentMonth]} {currentYear} — {totalEntries} jadwal, {uniqueKaryawan} karyawan, {uniqueDays} hari</p>
          </div>
          <div className="page-header-actions" style={{ gap: 8 }}>
            {hasMenuAction('jadwal_kerja', 'create') && (
              <button className="btn btn-primary" onClick={() => { setAddForm({ karyawan_ids: [], tanggal_awal: todayStr(), tanggal_akhir: todayStr(), jam_masuk: '07:00', jam_pulang: '15:00' }); setError(''); setShowAddModal(true); }}>
                <Plus size={16} /><span>Tambah Jadwal</span>
              </button>
            )}
            {monthData.length > 0 && (
              <button className="btn btn-secondary" onClick={generateMonthlyPDF}>
                <Printer size={16} /><span>Export PDF</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Month Navigation + Search */}
      <div className="filters" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button className="btn btn-secondary" style={{ padding: '6px 10px', minHeight: 36 }} onClick={prevMonth}>
            <ChevronLeft size={18} />
          </button>
          <span style={{ fontWeight: 700, fontSize: '1rem', minWidth: 140, textAlign: 'center' }}>
            {MONTHS[currentMonth]} {currentYear}
          </span>
          <button className="btn btn-secondary" style={{ padding: '6px 10px', minHeight: 36 }} onClick={nextMonth}>
            <ChevronRight size={18} />
          </button>
          {/* Hari Ini pindah ke filter bar bawah */}
        </div>
        <div className="search-box" style={{ flex: 1, minWidth: 200, marginBottom: 0 }}>
          <Search size={16} />
          <input type="text" placeholder="Cari karyawan..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="stat-mini"><span className="stat-mini-value" style={{ color: '#11cdef' }}>{totalEntries}</span> Total Jadwal</div>
        <div className="stat-mini"><span className="stat-mini-value" style={{ color: '#2dce89' }}>{uniqueKaryawan}</span> Karyawan</div>
        <div className="stat-mini"><span className="stat-mini-value" style={{ color: '#8965e0' }}>{uniqueDays}</span> Hari Kerja</div>
      </div>

      {/* Filter Bar: Date + Status + Bagian */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
            Tanggal:
          </label>
          <input type="date" value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            style={{
              padding: '6px 10px', borderRadius: 6, border: '1px solid var(--gray-200)',
              fontSize: '0.8rem', fontWeight: 500,
            }} />
          <button className="btn btn-secondary"
            style={{ padding: '4px 12px', minHeight: 32, fontSize: '0.75rem' }}
            onClick={goToday}>
            Hari Ini
          </button>
          {filterDate && (
            <button className="btn btn-secondary"
              style={{ padding: '4px 8px', minHeight: 32, fontSize: '0.7rem', color: 'var(--gray-400)' }}
              onClick={() => setFilterDate('')}>
              <X size={14} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', marginRight: 4 }}>Status:</span>
          <button onClick={() => setFilterStatus('')}
            style={{
              padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: '0.75rem', fontWeight: filterStatus === '' ? 700 : 500,
              background: filterStatus === '' ? 'var(--gray-700)' : 'var(--gray-100)',
              color: filterStatus === '' ? '#fff' : 'var(--gray-600)',
            }}>Semua</button>
          {STATUS_OPTIONS.map(s => (
            <button key={s.code} onClick={() => setFilterStatus(filterStatus === s.code ? '' : s.code)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '4px 8px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: '0.75rem', fontWeight: filterStatus === s.code ? 700 : 500,
                background: filterStatus === s.code ? s.bg : 'var(--gray-100)',
                color: filterStatus === s.code ? s.text : 'var(--gray-500)',
                borderBottom: filterStatus === s.code ? `2px solid ${s.text}` : '2px solid transparent',
              }}>{s.code}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', marginRight: 4 }}>Bagian:</span>
          <select value={filterBagian} onChange={e => setFilterBagian(e.target.value)}
            style={{
              padding: '4px 10px', borderRadius: 6, border: '1px solid var(--gray-200)',
              fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
              background: '#fff',
            }}>
            <option value="">Semua Bagian</option>
            {bagianList.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          {filterBagian && (
            <button className="btn btn-secondary"
              style={{ padding: '4px 8px', minHeight: 32, fontSize: '0.7rem', color: 'var(--gray-400)' }}
              onClick={() => setFilterBagian('')}>
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Today's attendance summary */}
      {filterDate && (
        <div style={{
          display: 'flex', gap: 8, marginBottom: 12, padding: '10px 14px',
          background: '#f8f9fa', borderRadius: 'var(--radius)',
          border: '1px solid var(--gray-200)', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)' }}>
            📋 Kehadiran {filterDate}:
          </span>
          {STATUS_OPTIONS.map(s => {
            const count = todayStatusCounts[s.code] || 0;
            if (count === 0) return null;
            return (
              <span key={s.code} style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 8px', borderRadius: 4,
                background: filterStatus === s.code ? s.bg : '#fff',
                border: `1px solid ${s.bg}`,
                fontSize: '0.75rem', fontWeight: 600, color: s.text,
              }}>
                {s.code}: <strong>{count}</strong>
              </span>
            );
          })}
          {todayEntries.length === 0 && (
            <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Belum ada data</span>
          )}
          <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginLeft: 'auto' }}>
            {filteredCount} karyawan ditampilkan
          </span>
        </div>
      )}

      {/* Monthly Matrix Grid */}
      {loading ? (
        <div className="page-loading"><div className="spinner-lg" /><p>Memuat data...</p></div>
      ) : (
        <div className="table-wrapper" style={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
          {filterDate ? (
            /* Single-date view: info + status inline di samping nama */
            <table className="data-table matrix-table" style={{ minWidth: 320 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: '0.85rem', borderBottom: '2px solid var(--gray-300)' }}>
                    Karyawan
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredKaryawan.length === 0 ? (
                  <tr><td style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    {karyawan.length === 0 ? 'Belum ada data karyawan.' : 'Tidak ada jadwal untuk tanggal ini.'}
                  </td></tr>
                ) : (
                  filteredKaryawan.map(k => {
                    const entry = scheduleMap[k.id]?.[filterDate];
                    const parts = filterDate.split('-');
                    const fdYear = parseInt(parts[0], 10);
                    const fdMonth = parseInt(parts[1], 10);
                    const fdDay = parseInt(parts[2], 10);
                    const d = new Date(fdYear, fdMonth - 1, fdDay);
                    const dayName = DAYS[d.getDay()];
                    const fdLabel = dayName + ' ' + fdDay + '/' + fdMonth;
                    const isWeekend = d.getDay() === 0;
                    const isToday = filterDate === today;

                    return (
                      <tr key={k.id}
                        onClick={() => openStatusPicker(k.id, filterDate)}
                        style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseOver={e => e.currentTarget.style.background = '#f8f9fa'}
                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{
                          padding: '8px 12px', fontWeight: 600, fontSize: '0.85rem',
                          borderBottom: '1px solid var(--gray-300)',
                          verticalAlign: 'middle',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 400 }}>
                                {k.nik || ''}
                              </span>
                              {' '}{k.nama}
                              {k.bagian_name && <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginLeft: 6 }}>({k.bagian_name})</span>}
                            </span>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '2px 8px', borderRadius: 4,
                              background: isToday ? 'rgba(17,205,239,0.08)' : (isWeekend ? '#fff1f2' : '#f8f9fa'),
                              color: isWeekend ? '#f43f5e' : 'var(--gray-500)',
                              fontSize: '0.7rem', fontWeight: 500,
                            }}>
                              {fdLabel}
                            </span>
                            {entry ? (() => {
                              const statusCode = entry.keterangan || 'H';
                              const st = getStatusStyle(statusCode);
                              return (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '2px 10px', borderRadius: 12,
                                  background: st.bg, color: st.text,
                                  fontSize: '0.78rem', fontWeight: 700,
                                }}>
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 22, height: 22, borderRadius: '50%',
                                    background: st.bg, color: st.text,
                                    fontSize: '0.7rem', fontWeight: 700,
                                  }}>{statusCode}</span>
                                  {st.label}
                                </span>
                              );
                            })(                            ) : (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '2px 10px', borderRadius: 12,
                                background: '#d1fae5', color: '#065f46',
                                fontSize: '0.78rem', fontWeight: 700,
                              }}>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 22, height: 22, borderRadius: '50%',
                                  background: '#d1fae5', color: '#065f46',
                                  fontSize: '0.7rem', fontWeight: 700,
                                }}>H</span>
                                Hadir
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          ) : (
            /* Full month matrix */
            <table className="data-table matrix-table" style={{ minWidth: days * 48 + 200 }}>
              <thead>
                <tr>
                  <th className="matrix-sticky-col" style={{ minWidth: 180, position: 'sticky', left: 0, zIndex: 3, background: 'var(--card-bg)' }}>
                    Karyawan
                  </th>
                  {Array.from({ length: days }, (_, i) => {
                    const day = i + 1;
                    const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
                    const d = new Date(currentYear, currentMonth, day);
                    const dayName = DAYS[d.getDay()];
                    const isToday = dateStr === today;
                    return (
                      <th key={day}
                        className={isToday ? 'matrix-today-header' : ''}
                        style={{
                          width: 44, minWidth: 44, textAlign: 'center', padding: '4px 2px',
                          fontSize: '0.7rem', fontWeight: 600,
                          color: d.getDay() === 0 ? '#f43f5e' : (isToday ? '#11cdef' : '#64748b'),
                          background: d.getDay() === 0 ? '#fff1f2' : (isToday ? 'rgba(17,205,239,0.04)' : 'transparent'),
                          borderLeft: '1px solid var(--gray-100)',
                        }}>
                        <div style={{ fontSize: '0.6rem', textTransform: 'uppercase' }}>{dayName}</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{day}</div>
                      </th>
                    );
                  })}
                  {/* Summary header — H | I | S | C | O | A | B | Total */}
                  <th style={{
                    position: 'sticky', right: 0, zIndex: 3,
                    background: 'var(--card-bg)',
                    minWidth: 363, width: 363,
                    padding: 0,
                    borderLeft: '2px solid var(--gray-200)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'stretch', height: '100%' }}>
                      {STATUS_OPTIONS.map(s => (
                        <div key={s.code} style={{
                          width: 44, minWidth: 44,
                          padding: '4px 1px',
                          textAlign: 'center',
                          fontSize: '0.7rem', fontWeight: 700,
                          color: s.text,
                          background: s.bg,
                          borderRight: '1px solid rgba(0,0,0,0.06)',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {s.code}
                        </div>
                      ))}
                      <div style={{
                        width: 55, minWidth: 55,
                        padding: '4px 1px',
                        textAlign: 'center',
                        fontSize: '0.7rem', fontWeight: 700,
                        color: '#374151',
                        background: '#e5e7eb',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      }}>
                        Total
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredKaryawan.length === 0 ? (
                  <tr><td colSpan={days + 2} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                    {karyawan.length === 0 ? 'Belum ada data karyawan.' : 'Karyawan tidak ditemukan.'}
                  </td></tr>
                ) : (
                  filteredKaryawan.map(k => {
                    const entries = scheduleMap[k.id] || {};
                    // Per-employee status counts
                    const empCounts = {};
                    STATUS_OPTIONS.forEach(s => { empCounts[s.code] = 0; });
                    for (let _d2 = 1; _d2 <= days; _d2++) {
                      const ds = `${selectedMonth}-${String(_d2).padStart(2, '0')}`;
                      const entry = entries[ds];
                      const code = entry ? (entry.keterangan || 'H') : 'H';
                      if (empCounts[code] !== undefined) empCounts[code]++;
                    }
                    return (
                      <tr key={k.id}>
                        <td className="matrix-sticky-col" style={{
                          position: 'sticky', left: 0, zIndex: 1,
                          background: 'var(--card-bg)', fontWeight: 600, fontSize: '0.85rem',
                          borderRight: '2px solid var(--gray-200)',
                        }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 400 }}>
                            {k.nik || ''}
                          </span>
                          {' '}{k.nama}
                          {k.bagian_name && <span style={{ fontSize: '0.7rem', color: 'var(--gray-400)', marginLeft: 6 }}>({k.bagian_name})</span>}
                        </td>
                        {Array.from({ length: days }, (_, i) => {
                          const day = i + 1;
                          const dateStr = `${selectedMonth}-${String(day).padStart(2, '0')}`;
                          const entry = entries[dateStr];
                          const isToday = dateStr === today;
                          const d = new Date(currentYear, currentMonth, day);
                          const isWeekend = d.getDay() === 0; // Sunday

                          return (
                            <td key={day}
                              onClick={() => openStatusPicker(k.id, dateStr)}
                              className="matrix-cell"
                              style={{
                                width: 44, minWidth: 44, height: 40,
                                textAlign: 'center', cursor: 'pointer',
                                fontSize: '0.85rem', fontWeight: 700,
                                borderLeft: '1px solid var(--gray-50)',
                                borderBottom: '1px solid var(--gray-50)',
                                background: isToday ? 'rgba(17,205,239,0.06)' : (isWeekend ? '#fff1f2' : 'transparent'),
                                color: isWeekend ? '#f43f5e' : 'inherit',
                                transition: 'background 0.15s',
                              }}
                              title={`${k.nama} — ${day} ${MONTHS[currentMonth]} ${currentYear}${entry ? ' (' + (entry.keterangan || 'H') + ')' : ' (H)'}`}>
                              {entry ? (() => {
                                const statusCode = entry.keterangan || 'H';
                                const st = getStatusStyle(statusCode);
                                return (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 28, height: 28, borderRadius: '50%',
                                    background: st.bg, color: st.text,
                                    fontSize: '0.78rem', fontWeight: 700,
                                  }}>{statusCode}</span>
                                );
                              })() : (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 28, height: 28, borderRadius: '50%',
                                  background: '#d1fae5', color: '#065f46',
                                  fontSize: '0.78rem', fontWeight: 700,
                                }}>H</span>
                              )}
                            </td>
                          );
                        })}
                        {/* Status summary — H | I | S | C | O | A | B | Total */}
                        <td style={{
                          position: 'sticky', right: 0, zIndex: 1,
                          background: 'var(--card-bg)',
                          borderLeft: '2px solid var(--gray-200)',
                          borderBottom: '1px solid var(--gray-50)',
                          padding: 0,
                          verticalAlign: 'middle',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'stretch' }}>
                            {STATUS_OPTIONS.map(s => (
                              <div key={s.code} style={{
                                width: 44, minWidth: 44,
                                padding: '6px 1px',
                                textAlign: 'center',
                                fontSize: '0.78rem', fontWeight: 700,
                                color: empCounts[s.code] > 0 ? s.text : '#cbd5e1',
                                background: empCounts[s.code] > 0 ? s.bg : 'transparent',
                                borderRight: '1px solid var(--gray-50)',
                                transition: 'all 0.1s',
                              }}>
                                {empCounts[s.code]}
                              </div>
                            ))}
                            <div style={{
                              width: 55, minWidth: 55,
                              padding: '6px 1px',
                              textAlign: 'center',
                              fontSize: '0.78rem', fontWeight: 700,
                              color: '#374151',
                              background: '#f9fafb',
                            }}>
                              {STATUS_OPTIONS.reduce((sum, s) => sum + empCounts[s.code], 0)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Status Picker Modal */}
      {showPicker && (
        <div className="modal-overlay" onClick={() => setShowPicker(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>{pickerData.entryId ? 'Ubah Jadwal' : 'Pilih Status'}</h3>
              <button className="btn-icon" onClick={() => setShowPicker(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.9rem', color: 'var(--gray-500)', marginBottom: 14 }}>
                <strong>{pickerData.nama}</strong>
                <span style={{ color: 'var(--gray-400)', margin: '0 6px' }}>—</span>
                {pickerData.tanggal}
              </p>

              {/* Jam Masuk/Pulang Editor */}
              {pickerData.entryId && (
                <div style={{
                  marginBottom: 16,
                  padding: '12px 14px',
                  background: '#f8f9fa',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--gray-200)',
                }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: 8 }}>
                    Jam Kerja
                  </label>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.7rem', color: 'var(--gray-400)', display: 'block', marginBottom: 2 }}>Masuk</label>
                      <input type="time" value={jamEdit.masuk}
                        onChange={e => setJamEdit(prev => ({ ...prev, masuk: e.target.value }))}
                        style={{
                          width: '100%', padding: '8px 10px',
                          border: '1px solid var(--gray-200)', borderRadius: 6,
                          fontSize: '0.85rem', fontWeight: 600,
                          background: '#fff',
                        }} />
                    </div>
                    <span style={{ color: 'var(--gray-300)', fontSize: '1.2rem', marginTop: 14 }}>→</span>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.7rem', color: 'var(--gray-400)', display: 'block', marginBottom: 2 }}>Pulang</label>
                      <input type="time" value={jamEdit.pulang}
                        onChange={e => setJamEdit(prev => ({ ...prev, pulang: e.target.value }))}
                        style={{
                          width: '100%', padding: '8px 10px',
                          border: '1px solid var(--gray-200)', borderRadius: 6,
                          fontSize: '0.85rem', fontWeight: 600,
                          background: '#fff',
                        }} />
                    </div>
                    <button onClick={handleJamSave}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center',
                        padding: '8px 14px', marginTop: 14,
                        background: 'var(--primary)', color: '#fff',
                        border: 'none', borderRadius: 6,
                        fontSize: '0.78rem', fontWeight: 600,
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>
                      Simpan
                    </button>
                  </div>
                </div>
              )}

              <p style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginBottom: 10, fontWeight: 500 }}>
                Status {pickerData.entryId ? `(saat ini: ${pickerData.currentStatus})` : ''}
                {pickerData.entryId && <span style={{ color: 'var(--gray-300)', fontWeight: 400, marginLeft: 4 }}>— klik status yang sama untuk hapus</span>}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {STATUS_OPTIONS.map(s => (
                  <button key={s.code}
                    onClick={() => handleStatusPick(s.code)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 12px',
                      border: pickerData.currentStatus === s.code ? '3px solid ' + s.text : '1px solid var(--gray-200)',
                      borderRadius: 'var(--radius)',
                      background: pickerData.currentStatus === s.code ? s.bg : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.1s',
                      fontSize: '0.82rem',
                      fontWeight: pickerData.currentStatus === s.code ? 700 : 500,
                      color: 'var(--gray-700)',
                    }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 30, height: 30, borderRadius: '50%',
                      background: s.bg, color: s.text,
                      fontWeight: 700, fontSize: '0.8rem',
                      flexShrink: 0,
                    }}>{s.code}</span>
                    <span>{s.label}</span>
                  </button>
                ))}
              </div>
              {pickerData.entryId && (
                <button className="btn btn-danger btn-block"
                  style={{ marginTop: 14, minHeight: 40 }}
                  onClick={() => {
                    setShowPicker(false);
                    if (window.confirm(`Hapus jadwal ${pickerData.nama} tanggal ${pickerData.tanggal}?`)) {
                      deleteJadwalKerja(pickerData.entryId).then(() => loadMonth()).catch(err => alert('Gagal: ' + err.message));
                    }
                  }}>
                  <Trash2 size={15} /> Hapus Jadwal
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tambah Jadwal Modal — Multi-Karyawan + Date Range */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <h3>Tambah Jadwal</h3>
              <button className="btn-icon" onClick={() => setShowAddModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ flex: 1, overflow: 'auto' }}>
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label>Tanggal Mulai *</label>
                  <input type="date" value={addForm.tanggal_awal}
                    onChange={e => setAddForm({...addForm, tanggal_awal: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Tanggal Akhir *</label>
                  <input type="date" value={addForm.tanggal_akhir}
                    onChange={e => setAddForm({...addForm, tanggal_akhir: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Jam Masuk</label>
                  <input type="time" value={addForm.jam_masuk}
                    onChange={e => setAddForm({...addForm, jam_masuk: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Jam Pulang</label>
                  <input type="time" value={addForm.jam_pulang}
                    onChange={e => setAddForm({...addForm, jam_pulang: e.target.value})} />
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Pilih Karyawan *</label>
                  <button className="btn btn-secondary" style={{ padding: '4px 12px', minHeight: 32, fontSize: '0.75rem' }}
                    onClick={selectAllKaryawan}>
                    Pilih Semua
                  </button>
                </div>
                <div style={{
                  maxHeight: 240, overflowY: 'auto',
                  border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)', padding: 4,
                }}>
                  {karyawan.map(k => (
                    <label key={k.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                      borderRadius: 6, cursor: 'pointer', transition: 'background 0.1s',
                    }}
                      onMouseOver={e => e.currentTarget.style.background = '#f8f9fa'}
                      onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                      <input type="checkbox" checked={addForm.karyawan_ids.includes(k.id)}
                        onChange={() => toggleKaryawan(k.id)} />
                      <span style={{ fontSize: '0.85rem' }}>
                        <strong>{k.nama}</strong>
                        {k.bagian_name ? <span style={{ color: 'var(--gray-400)', marginLeft: 6 }}>— {k.bagian_name}</span> : ''}
                      </span>
                    </label>
                  ))}
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 6 }}>
                  Terpilih: <strong>{addForm.karyawan_ids.length}</strong> karyawan
                  {' × '}
                  {(() => {
                    const a = new Date(addForm.tanggal_awal);
                    const b = new Date(addForm.tanggal_akhir);
                    const hari = Math.max(0, Math.floor((b - a) / (1000*60*60*24)) + 1);
                    return <strong>{hari}</strong>;
                  })()} hari
                  = <strong>{addForm.karyawan_ids.length * Math.max(0, Math.floor((new Date(addForm.tanggal_akhir) - new Date(addForm.tanggal_awal)) / (1000*60*60*24)) + 1)}</strong> jadwal
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleBulkAdd} disabled={saving}>
                {saving ? 'Membuat...' : `Simpan (${addForm.karyawan_ids.length} karyawan)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
